import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, History, Loader2, RotateCcw, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useSite } from "@/context/SiteContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const ModeChip = ({ mode }) => {
  const styles =
    mode === "live"
      ? "bg-[#e9f1fd] text-[#0050c0] border-[#c8dcf7]"
      : mode === "rollback"
        ? "bg-[#fdf7e1] text-[#8a6d00] border-[#f0e3ae]"
        : "bg-[#fef2e5] text-[#c25a00] border-[#f8ddc0]";
  return (
    <span className={`rounded-[5px] border px-1 py-0 font-nunito text-[8px] font-extrabold uppercase tracking-[0.1em] ${styles}`}>
      {mode}
    </span>
  );
};

/** Recent deploy runs for a workspace with one-tap snapshot rollback. */
export const DeployHistory = ({ siteId }) => {
  const { fetchHealth } = useSite();
  const [deploys, setDeploys] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmId, setConfirmId] = useState(null); // job pending inline confirm
  const [rollingBack, setRollingBack] = useState(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sites/${siteId}/deploys?limit=5`);
      setDeploys(data.deploys || []);
    } catch (e) {
      setDeploys([]);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    load();
  }, [load]);

  const rollback = async (job) => {
    setRollingBack(job.job_id);
    setConfirmId(null);
    try {
      const { data } = await axios.post(`${API}/sites/${siteId}/rollback`, { job_id: job.job_id });
      toast.success(`Rolled back "${job.target}" — the previous version is live again.`, {
        action: data.live_link
          ? { label: "View", onClick: () => window.open(data.live_link, "_blank", "noopener") }
          : undefined,
      });
      await load();
      fetchHealth(siteId, true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Rollback failed.");
    } finally {
      setRollingBack(null);
    }
  };

  if (loading && deploys === null) {
    return (
      <div data-testid="deploy-history-loading" className="mt-2 space-y-1.5">
        <Skeleton className="h-7 bg-black/10" />
        <Skeleton className="h-7 bg-black/10" />
        <Skeleton className="h-7 bg-black/10" />
      </div>
    );
  }

  return (
    <div data-testid="deploy-history-panel" className="mt-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 font-nunito text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#9a9a9a]">
          <History size={11} /> Recent Deploys
        </span>
        <button
          type="button"
          aria-label="Refresh deploy history"
          data-testid="deploy-history-refresh-button"
          onClick={load}
          className="text-[#9a9a9a] hover:text-[#383838] transition-colors duration-150"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {deploys && deploys.length === 0 && (
        <p data-testid="deploy-history-empty" className="mt-1.5 text-[10px] text-[#9a9a9a]">
          No deploys yet for this workspace.
        </p>
      )}

      <div className="mt-1.5 space-y-1">
        {(deploys || []).map((j) => (
          <div
            key={j.job_id}
            data-testid={`deploy-history-row-${j.job_id}`}
            className="rounded-[8px] border border-black/[0.07] bg-black/[0.02] px-2 py-1.5"
          >
            {confirmId === j.job_id ? (
              <div className="flex items-center justify-between gap-2" data-testid="deploy-history-confirm-row">
                <span className="font-nunito text-[9.5px] font-extrabold uppercase tracking-[0.08em] text-[#c25a00] truncate">
                  Restore pre-deploy version of “{j.target}”?
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    data-testid="deploy-history-rollback-confirm-yes"
                    onClick={() => rollback(j)}
                    className="rounded-[6px] bg-[#f07000] px-2 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.1em] text-white hover:bg-[#d96400] transition-colors duration-150"
                  >
                    Yes, Roll Back
                  </button>
                  <button
                    type="button"
                    data-testid="deploy-history-rollback-confirm-no"
                    onClick={() => setConfirmId(null)}
                    className="rounded-[6px] border border-black/15 px-2 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.1em] text-[#6e6e6e] hover:text-[#383838] transition-colors duration-150"
                  >
                    No
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-nunito text-[10.5px] font-extrabold text-[#383838] truncate">{j.target}</span>
                    <ModeChip mode={j.mode} />
                  </div>
                  <span className="block text-[9px] text-[#9a9a9a]">
                    <span className={j.status === "completed" ? "text-[#0050c0]" : j.status === "failed" ? "text-[#c25a00] font-bold" : "text-[#b45309]"}>
                      {j.status}
                    </span>{" "}
                    · {timeAgo(j.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {j.live_link && (
                    <a
                      href={j.live_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View live page"
                      data-testid="deploy-history-view-link"
                      className="text-[#9a9a9a] hover:text-[#383838] transition-colors duration-150"
                    >
                      <ExternalLink size={12} />
                    </a>
                  )}
                  {j.rollback_eligible &&
                    (rollingBack === j.job_id ? (
                      <Loader2 size={12} className="animate-spin text-[#f07000]" data-testid="deploy-history-rollback-spinner" />
                    ) : (
                      <button
                        type="button"
                        data-testid="deploy-history-rollback-button"
                        onClick={() => setConfirmId(j.job_id)}
                        disabled={!!rollingBack}
                        className="inline-flex items-center gap-1 rounded-[6px] border border-[#f07000]/40 px-1.5 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.1em] text-[#c25a00] hover:bg-[#f07000]/10 transition-colors duration-150 disabled:opacity-50"
                      >
                        <RotateCcw size={10} />
                        Rollback
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
